import './signer-input-card.scss'

import { defineComponent } from 'vue'
import { faCheck, faEraser, faTimesCircle } from '@fortawesome/free-solid-svg-icons'

import { Signer, TranslatedString, getTranslatedString } from '@skinsoft/types'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { ThesaurusTermDataStore } from '@skinsoft/entity'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { FreeDrawingTool } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox'
import { SAVE_CURRENT_REPORT_TOPIC } from '../../../../busTopics'
import { getCurrentDateAsSkinDate } from '@skinsoft/utils'

export interface SignatureDrawing {
    canvas: { width: number, height: number },
    signature: any[]
}

// Kept as module-level constants (not component data) since they never change; exposed as static
// properties below because configure-vue.ts reads them off the component itself.
const DEFAULT_SIGNATURE_CANVAS_WIDTH = 700;
const DEFAULT_SIGNATURE_CANVAS_HEIGHT = 300;
const STROKE_WIDTH = 5;
const STROKE_COLOR = 'black';

export const SignerInputCard = defineComponent({
    mixins: [reactiveVueMixin],
    props: ['signer'],
    template: require('./signer-input-card.html'),
    data() {
        return {
            shapes: {
                canvas: {
                    height: DEFAULT_SIGNATURE_CANVAS_HEIGHT,
                    width: DEFAULT_SIGNATURE_CANVAS_WIDTH
                }, signature: []
            } as SignatureDrawing,

            // Icons
            eraseIcon: faEraser,
            checkIcon: faCheck,
            crossIcon: faTimesCircle,

            // Drawing data
            configKonva: {
                height: DEFAULT_SIGNATURE_CANVAS_HEIGHT,
                preventDefault: true,
                width: DEFAULT_SIGNATURE_CANVAS_WIDTH,
            },
            startClick: undefined as any,
            currentShape: undefined as any,
            freeDrawingTool: new FreeDrawingTool(STROKE_WIDTH, STROKE_COLOR),
            isDrawing: false,
            shapeIndex: 0,
            signatureOk: false,

            signerPosition: { translatedString: {} } as TranslatedString,
        }
    },
    mounted() {
        const stage = (this.$refs.stage as any).getStage();
        const stageWidth = stage.container().clientWidth;
        stage.width(stageWidth);
        this.shapes.canvas.width = stageWidth
        this.fetchSignerPosition()

        // Bound imperatively, not via template @mousedown etc.: <v-stage> reads listeners off its
        // own `attrs`, which @vue/compat MODE 2 excludes from a component's $attrs.
        stage.on('mousedown touchstart', this.onTouchStart)
        stage.on('mouseup touchend', this.onTouchEnd)
        stage.on('mousemove touchmove', this.onTouchMove)
    },
    beforeUnmount() {
        (this.$refs.stage as any)?.getStage()?.off('mousedown touchstart mouseup touchend mousemove touchmove')
    },
    computed: {
        signerName(): string {
            return getTranslatedString(this.signer.signer, this.getAppLocale())
        },
    },
    methods: {
        fetchSignerPosition() {
            if(this.signer.positionOfSignatory) {
                return ThesaurusTermDataStore.getInstance().fetchOne(this.signer.positionOfSignatory)
                    .subscribe(thesaurusTerm => {
                        this.signerPosition = thesaurusTermSelectors.preferredTerm(thesaurusTerm) ?? {translatedString: {}}
                    })
            }
        },
        saveSignature() {
            // only works because this.signer is an object
            // it is bad practice to change props directly, we should use an event
            this.signer.dateOfSignature = getCurrentDateAsSkinDate()
            this.emitOnBus(SAVE_CURRENT_REPORT_TOPIC)
        },
        clearSignature() {
            if (!this.isDrawing) {
                // only works because this.signer is an object
                // it is bad practice to change props directly, we should use an event
                this.shapes = this.blankSignatureDrawing()
                this.signer.dateOfSignature = undefined
            }
        },
        saveShapes() {
            // only works because this.signer is an object
            // it is bad practice to change props directly, we should use an event
            this.signer.dateOfSignature = getCurrentDateAsSkinDate()
            this.signer.signature = JSON.stringify(this.shapes);
        },
        // Drawing methods
        onTouchStart(event) {
            event.evt.preventDefault()
            event.evt.stopPropagation()
            if (event.currentTarget._pointerPositions) {
                this.startClick = {}
                this.startClick.x = event.currentTarget._changedPointerPositions[0].x;
                this.startClick.y = event.currentTarget._changedPointerPositions[0].y;

                this.currentShape = this.freeDrawingTool.addShape(this.startClick.x, this.startClick.y, this.shapeIndex)
                this.shapes.signature.push(this.currentShape)
                this.shapeIndex++;
                this.isDrawing = true;
            } else {
                this.startClick = undefined
            }
        },
        onTouchMove(event) {
            event.evt.preventDefault()
            event.evt.stopPropagation()
            if (this.isDrawing && event.currentTarget._changedPointerPositions) {

                const currentX = event.currentTarget._changedPointerPositions[0].x;
                const currentY = event.currentTarget._changedPointerPositions[0].y;

                this.freeDrawingTool.onTouchMove(this.currentShape, currentX, currentY, this.startClick.x, this.startClick.y);
            }
        },
        onTouchEnd(event) {
            event.evt.preventDefault()
            event.evt.stopPropagation()
            if (this.startClick && event.currentTarget._changedPointerPositions) {

                const endX = event.currentTarget._changedPointerPositions[0].x;
                const endY = event.currentTarget._changedPointerPositions[0].y;
                this.freeDrawingTool.onTouchEnd(this.currentShape, endX, endY, this.startClick.x, this.startClick.y)
                this.currentShape = null;
                this.saveShapes()
            }
            this.isDrawing = false;
        },
        blankSignatureDrawing(): SignatureDrawing {
            return {
                canvas: {
                    height: DEFAULT_SIGNATURE_CANVAS_HEIGHT,
                    width: DEFAULT_SIGNATURE_CANVAS_WIDTH,
                },
                signature: []
            } as SignatureDrawing
        },
    },
    watch: {
        signer: {
            handler(signer) {
                this.shapes = signer.signature ? JSON.parse(signer.signature) : this.blankSignatureDrawing();
                this.shapeIndex = this.shapes.signature.length;
                this.fetchSignerPosition()
            },
            immediate: true,
        },
        shapes: {
            handler() {
                this.signatureOk = this.shapes.signature.length > 0
                this.saveShapes()
            },
            immediate: true,
            deep: true,
        },
    },
})

// Preserved as an external API: apps/condition-report/src/configure-vue.ts reads these
// static dimensions off the component itself.
;(SignerInputCard as any).DEFAULT_SIGNATURE_CANVAS_WIDTH = DEFAULT_SIGNATURE_CANVAS_WIDTH
;(SignerInputCard as any).DEFAULT_SIGNATURE_CANVAS_HEIGHT = DEFAULT_SIGNATURE_CANVAS_HEIGHT
